/**
 * Process — dark band with the four execution steps:
 * scope → mobilisation → coordination → acceptance.
 */

import { memo } from "react";
import { useTranslation } from "react-i18next";
import Reveal from "@/components/common/Reveal";

interface Step {
  title: string;
  description: string;
}

const Process = () => {
  const { t } = useTranslation();
  const steps = t("home.process.steps", { returnObjects: true }) as Step[];

  return (
    <section className="bg-ink text-white py-20 md:py-28" aria-labelledby="process-heading">
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-end mb-12 md:mb-16">
          <Reveal className="lg:col-span-7">
            <div className="flex items-center gap-4 mb-5">
              <span className="rule-light w-10 shrink-0" aria-hidden="true" />
              <p className="eyebrow text-cobalt-bright">{t("home.process.label")}</p>
            </div>
            <h2 id="process-heading" className="display-lg text-white">
              {t("home.process.heading")}
            </h2>
          </Reveal>
          <Reveal delay={90} className="lg:col-span-5">
            <p className="text-base md:text-lg text-white/70 lg:border-l lg:border-white/20 lg:pl-6">
              {t("home.process.intro")}
            </p>
          </Reveal>
        </div>

        <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-t border-white/20">
          {steps.map((step, index) => (
            <Reveal
              as="li"
              key={step.title}
              delay={index * 90}
              className="relative border-b border-white/20 lg:border-b-0 lg:border-r last:lg:border-r-0 md:odd:border-r md:odd:border-white/20 px-0 md:px-6 lg:px-7 first:lg:pl-0 py-7 md:py-9"
            >
              <span className="index-num block text-3xl text-cobalt-bright mb-4">
                {String(index + 1).padStart(2, "0")}
              </span>
              <h3 className="text-lg md:text-xl font-bold tracking-tight text-white mb-2">
                {step.title}
              </h3>
              <p className="text-sm leading-relaxed text-white/70">{step.description}</p>
            </Reveal>
          ))}
        </ol>
      </div>
    </section>
  );
};

export default memo(Process);
